import clsx from 'clsx';
import { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { D, formatWholeUah } from '../../helpers/taxPlanner/money';
import { recalculate, showToast } from '../../store/slices/taxPlannerSlice';
import Badge from '../Badge';
import Card from '../Card';
import DirtyBar from '../DirtyBar';
import LinkButton from '../LinkButton';
import SaleFocus from '../SaleFocus';
import { jurisdiction } from '../../config/taxPlannerJurisdiction';
import { POSITIONS_ID } from './PositionsTable';
import { pluralForm } from '../../helpers/pluralForm';

const MINUS = '−';
const TRADING = 'TRADING';


const uah = (value) => `${formatWholeUah(value)} ${jurisdiction.currencySymbol}`;
// A value that rounds to a whole 0 carries no sign.
const signedUah = (value) => (D(value).lt(0) && formatWholeUah(value) !== '0' ? `${MINUS}${uah(D(value).abs())}` : uah(value));

const signOf = (value) => {
  const whole = formatWholeUah(D(value).abs());
  if (whole === '0') return undefined;
  return D(value).gt(0) ? 'pos' : 'neg';
};

// Mock `.tile`: muted tile; `key` (success) when the sale lowers the tax, `danger` when it raises it.
function StatTile({ id, label, value, tone, rows, note, valueSign }) {
  return (
    <div
      data-testid={`summary-tile-${id}`}
      data-tone={tone}
      className={clsx(
        'rounded-xl border border-border px-4 py-3.5',
        tone === 'key' ? 'bg-success-bg' : tone === 'danger' ? 'bg-danger-bg' : 'bg-surface-muted',
      )}>
      <p className={clsx('text-xs font-medium', tone === 'key' ? 'text-success-ink' : 'text-text-muted')}>{label}</p>
      <p
        data-testid="tile-value"
        className={clsx(
          'num mt-1 text-[26px] font-semibold tracking-[-.02em]',
          valueSign === 'pos' ? 'text-success-ink' : valueSign === 'neg' ? 'text-danger' : 'text-text',
        )}>
        {value}
      </p>
      {rows && (
        <ul className="num mt-2.5 flex flex-col gap-1 text-xs">
          {rows.map((r) => (
            <li key={r.type} data-testid={`tax-row-${r.type}`} className="flex justify-between gap-3">
              <span className="text-text-muted">{r.label}</span> <span className="text-text">{r.value}</span>
            </li>
          ))}
        </ul>
      )}
      {note && <p className="mt-2.5 text-xs text-text-muted">{note}</p>}
    </div>
  );
}

const ArrowDown = () => (
  <svg aria-hidden="true" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M12 5v14M6 13l6 6 6-6" />
  </svg>
);

const Chevron = ({ open }) => (
  <svg aria-hidden="true" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={clsx('transition-transform', open && 'rotate-90')}>
    <path d="M9 6l6 6-6 6" />
  </svg>
);

function SummaryBlock() {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const snapshot = useSelector((state) => state.taxPlanner.snapshot);
  const totals = useSelector((state) => state.taxPlanner.committedTotals);
  const dirty = useSelector((state) => state.taxPlanner.dirty);
  const [breakdownOpen, setBreakdownOpen] = useState(false);
  if (!snapshot || !totals) return null;

  const k = (key, opts) => t(`taxPlanner.scr02.${key}`, opts);
  const plural = (key, count) => k(`${key}_${pluralForm(count, i18n.language)}`, { count });
  const { taxTypes } = jurisdiction;
  const year = snapshot.taxYear;
  const yearEnd = `31.12.${year}`;

  const taxRows = (liability) => taxTypes.map((type) => ({ type, label: k(`summary.taxTypes.${type}`), value: uah(liability[type]) }));
  const increase = totals.taxIncrease;
  const reduces = !increase && signOf(totals.reducedBy) === 'pos';
  const reducedValue = increase ? `+${uah(D(totals.reducedBy).abs())}` : uah(totals.reducedBy);
  let reducedNote = k('summary.reducedPick');
  if (increase) reducedNote = k('summary.increaseNote');
  else if (reduces) reducedNote = k('summary.reducedHint', { date: yearEnd });

  // The loss behind the committed numbers: base result = trading income − loss = afterSale − selectedResult.
  const base = D(totals.afterSale).minus(totals.selectedResult);
  const loss = D(totals.tradingResult).minus(base);
  const baseNotPositive = base.lte(0);
  const afterSaleNegative = D(totals.afterSale).lt(0);

  const terms = [{ label: k('focus.yearResult'), value: signedUah(totals.tradingResult), sign: signOf(totals.tradingResult) }];
  if (loss.gt(0)) terms.push({ op: MINUS, label: k('focus.priorLoss', { year: year - 1 }), value: uah(loss) });
  terms.push(
    { op: '+', label: k('focus.saleResult'), value: signedUah(totals.selectedResult), sign: signOf(totals.selectedResult), emphasis: true },
    { op: '=', label: k('focus.afterSale'), value: signedUah(totals.afterSale), sign: signOf(totals.afterSale) },
  );

  const count = totals.selectedCount;
  let head;
  if (count === 0 && baseNotPositive) {
    head = (
      <>
        <b>{k('focus.zeroHead')}</b> {k('focus.zeroText')}
      </>
    );
  } else if (count === 0) {
    head = (
      <>
        <b>{k('focus.pickHead')}</b>
        {k('focus.pickText')}
      </>
    );
  } else {
    head = (
      <>
        <b>{k(`focus.lead_${pluralForm(count, i18n.language)}`, { count, date: yearEnd })}</b>
        {k('focus.leadTail')}
      </>
    );
  }
  const action = (
    <LinkButton onClick={() => document.getElementById(POSITIONS_ID)?.scrollIntoView?.({ behavior: 'smooth', block: 'start' })}>
      {k('focus.view')}
      <ArrowDown />
    </LinkButton>
  );

  const notes = [];
  if (count > 0 && increase) {
    notes.push(
      <p key="inc" className="text-danger">
        {k('focus.increaseNote')} <Badge size="md" tone="danger">{k('focus.profitBadge')}</Badge>
      </p>,
    );
  } else if (afterSaleNegative) {
    notes.push(<p key="carry">{k('focus.carry', { amount: formatWholeUah(D(totals.afterSale).abs()), year: year + 1 })}</p>);
  }
  if (totals.noPriceCount > 0) notes.push(<p key="noprice">{plural('focus.noPrice', totals.noPriceCount)}</p>);

  const short = (type) => k(`summary.taxShort.${type}`);
  const rates = (def) => def.rates.map((r) => `${short(r.taxType)} ${D(r.rate).times(100).toFixed()}%`).join(' + ');
  const BucketTag = ({ reducible }) =>
    reducible ? <Badge size="md" tone="accent">{k('breakdown.reducible')}</Badge> : <Badge size="md" tone="muted">{k('breakdown.notReducible')}</Badge>;

  const breakdown = (
    <div className="mt-2.5 overflow-x-auto [contain:inline-size]">
      <table className="w-full min-w-[420px] border-collapse text-[13px]">
        <thead>
          <tr className="border-b border-border text-left text-[11px] font-semibold text-text-muted">
            <th className="px-2 py-1.5 font-semibold">{k('breakdown.income', { year })}</th>
            <th className="px-2 py-1.5 text-right font-semibold">{k('breakdown.amount')}</th>
            <th className="px-2 py-1.5 text-right font-semibold">{k('breakdown.tax', { taxes: taxTypes.map(short).join(' + ') })}</th>
          </tr>
        </thead>
        <tbody>
          {totals.buckets.map((b) => {
            const def = snapshot.realized.buckets.find((x) => x.bucket === b.bucket);
            const trading = b.bucket === TRADING;
            const income = trading ? D(totals.tradingResult) : D(def.income);
            const withLoss = trading && loss.gt(0);
            return (
              <Fragment key={b.bucket}>
                <tr data-testid={`breakdown-row-${b.bucket}`} className={clsx('align-top', !withLoss && 'border-b border-border')}>
                  <td className="p-2 text-text">
                    {k(`breakdown.buckets.${b.bucket}`)} <BucketTag reducible={b.reducible} />
                    {trading && (
                      <small className="mt-0.5 block text-[11px] text-text-muted">
                        {income.lt(0) ? k('breakdown.tradingNegative') : k('breakdown.tradingOneSum')}
                      </small>
                    )}
                  </td>
                  <td className={clsx('num whitespace-nowrap p-2 text-right', signOf(income) === 'neg' ? 'text-danger' : signOf(income) === 'pos' && 'text-success-ink')}>
                    {signedUah(income)}
                  </td>
                  <td className="num whitespace-nowrap p-2 text-right text-text">
                    {uah(b.current.total)}
                    <small className="mt-0.5 block text-[11px] text-text-muted">{rates(def)}</small>
                  </td>
                </tr>
                {withLoss && (
                  <tr data-testid="breakdown-loss-row" className="border-b border-border text-xs text-text-muted">
                    <td className="px-2 pb-2">{k('breakdown.withLoss', { year: year - 1 })}</td>
                    <td className="num whitespace-nowrap px-2 pb-2 text-right">{`${MINUS}${uah(loss)}`}</td>
                    <td />
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
        <tfoot>
          <tr data-testid="breakdown-total" className="font-semibold text-text">
            <td className="p-2">{k('breakdown.total')}</td>
            <td />
            <td className="num whitespace-nowrap p-2 text-right">{uah(totals.current.total)}</td>
          </tr>
        </tfoot>
      </table>
      <p className="mt-2 text-xs text-text-muted">{k('breakdown.nbu')}</p>
    </div>
  );

  return (
    <Card data-testid="summary-block" data-stale={dirty ? 'true' : 'false'} aria-live="polite" className="p-5">
      <p className="text-[11px] font-semibold uppercase tracking-[.06em] text-text-muted">{k('summary.eyebrow', { year })}</p>
      <div className={clsx('transition-opacity', dirty && 'opacity-[0.45]')}>
        <div className="mt-3 grid grid-cols-1 gap-3 mid:grid-cols-3">
          <StatTile id="current" label={k('summary.current')} value={uah(totals.current.total)} rows={taxRows(totals.current)} />
          <StatTile
            id="reduced"
            label={k('summary.reduced')}
            value={reducedValue}
            valueSign={increase ? 'neg' : reduces ? 'pos' : undefined}
            tone={increase ? 'danger' : reduces ? 'key' : undefined}
            note={reducedNote}
          />
          <StatTile id="possible" label={k('summary.possible')} value={uah(totals.possible.total)} rows={taxRows(totals.possible)} />
        </div>
        <div className="mt-4">
          <SaleFocus title={k('focus.title')} head={head} action={action} terms={terms} note={notes.length ? notes : null} tone={count > 0 && increase ? 'warn' : 'default'} />
        </div>
      </div>
      <details open={breakdownOpen} className="mt-3">
        <summary
          onClick={(e) => {
            e.preventDefault();
            setBreakdownOpen((v) => !v);
          }}
          className="inline-flex cursor-pointer list-none items-center gap-1.5 text-[13px] font-medium text-accent-solid hover:underline hover:underline-offset-2 [&::-webkit-details-marker]:hidden">
          {k('breakdown.title')}
          <Chevron open={breakdownOpen} />
        </summary>
        {breakdownOpen && breakdown}
      </details>
      <div className="mt-[18px] flex flex-wrap items-center justify-between gap-x-5 gap-y-2 border-t border-border pt-3.5 text-xs text-text-muted">
        <span>{k('summary.footer')}</span>
        {dirty && <Badge size="md" tone="warn">{k('summary.stale')}</Badge>}
      </div>
      {dirty && <DirtyBar message={k('summary.dirtyMessage')} actionLabel={k('summary.recalculate')} onRecalculate={() => {
            dispatch(recalculate());
            dispatch(showToast(t('taxPlanner.scr02.table.toast.recalculated')));
          }} />}
    </Card>
  );
}

export default SummaryBlock;
